import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/UI/PageTransition';
import { FaTrophy, FaMedal, FaCalendarAlt } from 'react-icons/fa';
import Navbar from '../components/Layout/Navbar';
import Avatar from '../components/UI/Avatar';
import LevelBadge from '../components/UI/LevelBadge';
import Button from '../components/UI/Button';
import PremiumBanner from '../components/UI/PremiumBanner';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';


function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('monde');
  const [players, setPlayers] = useState([]);
  const [monthLabel, setMonthLabel] = useState('');
  const [pastChampions, setPastChampions] = useState([]);
  const [loading, setLoading] = useState(true);

  const TABS = [
    { key: 'amis',    label: 'AMIS',    endpoint: '/leaderboard/friends' },
    { key: 'monde',   label: 'MONDE',   endpoint: '/leaderboard/global' },
    { key: 'semaine', label: 'SEMAINE', endpoint: '/leaderboard/weekly' },
    { key: 'mois',    label: 'MOIS',    endpoint: '/leaderboard/monthly' },
  ];

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const tab = TABS.find((t) => t.key === activeTab);
        const { data } = await api.get(tab.endpoint);
        setPlayers(data.leaderboard || data.players || []);
        if (activeTab === 'mois') {
          setMonthLabel(data.monthLabel || '');
          setPastChampions(data.pastChampions || []);
        }
      } catch {
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [activeTab]);

  const getRankDisplay = (rank) => {
    if (rank === 1) return { icon: '🥇', color: '#f0b429', bg: 'bg-yellow-500/20' };
    if (rank === 2) return { icon: '🥈', color: '#94a3b8', bg: 'bg-gray-400/20' };
    if (rank === 3) return { icon: '🥉', color: '#d97706', bg: 'bg-orange-600/20' };
    return { icon: null, color: '#6b7280', bg: '' };
  };

  const isCurrentUser = (p) => !!(user?.id && p.id === user.id);

  return (
    <PageTransition variant="slideIn">
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '80px' }}>
      {/* Header */}
      <div className="flex items-center justify-center gap-3 pt-8 pb-4 px-4">
        <FaTrophy
          size={28}
          className="text-qfl-gold"
          style={{ filter: 'drop-shadow(0 0 10px rgba(240,180,41,0.5))' }}
        />
        <h1
          className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
          style={{ fontFamily: "'Rajdhani', sans-serif", textShadow: '0 0 20px rgba(240,180,41,0.3)' }}
        >
          CLASSEMENT
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex mx-4 mb-4 bg-black/30 rounded-xl p-1 border border-white/10">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 py-2.5 rounded-lg font-bold text-xs uppercase tracking-wider transition-all duration-200 ${
              activeTab === key
                ? 'bg-qfl-gold text-black shadow-md'
                : 'text-white/50 hover:text-white/80'
            }`}
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Top 3 podium */}
      {!loading && players.length >= 3 && (activeTab === 'monde' || activeTab === 'mois') && (
        <div className="flex flex-col items-center">
          {activeTab === 'mois' && monthLabel && (
            <div className="flex items-center gap-2 mb-2">
              <FaCalendarAlt size={14} className="text-qfl-gold" />
              <span className="text-qfl-gold text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                {monthLabel}
              </span>
            </div>
          )}
          <div className="flex items-end justify-center gap-3 px-4 mb-5">
            <PodiumItem player={players[1]} rank={2} height={80} />
            <PodiumItem player={players[0]} rank={1} height={100} />
            <PodiumItem player={players[2]} rank={3} height={65} />
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 flex flex-col gap-2">
          {players.map((player, index) => {
            const rank = index + 1;
            const { icon, color, bg } = getRankDisplay(rank);
            const isMe = isCurrentUser(player);

            return (
              <motion.div
                key={player._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => !isMe && navigate(`/profile/${player.id || player._id}`)}
                className={`flex items-center gap-3 rounded-xl p-3 border transition-all ${
                  isMe
                    ? 'bg-qfl-gold/10 border-qfl-gold/40 shadow-[0_0_15px_rgba(240,180,41,0.15)]'
                    : `${bg} border-white/10 bg-white/5 cursor-pointer hover:bg-white/10`
                }`}
              >
                {/* Rang */}
                <div className="w-8 flex-shrink-0 flex items-center justify-center">
                  {icon ? (
                    <span className="text-xl">{icon}</span>
                  ) : (
                    <span
                      className="font-bold text-sm"
                      style={{ color, fontFamily: "'Rajdhani', sans-serif" }}
                    >
                      {rank}
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <Avatar user={player} size="sm" showBorder={isMe} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-bold text-sm truncate ${isMe ? 'text-qfl-gold' : 'text-white'}`}
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}
                    >
                      {player.pseudo}
                      {isMe && <span className="text-xs ml-1">(Vous)</span>}
                    </span>
                    <LevelBadge level={player.niveau || player.level || 1} size="xs" />
                  </div>
                </div>

                {/* Score */}
                <div className="flex-shrink-0 text-right">
                  <div
                    className={`font-black text-lg ${isMe ? 'text-qfl-gold' : 'text-white'}`}
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    {Number(player.total_score || player.score || 0).toLocaleString('fr-FR')}
                  </div>
                  <div className="text-white/40 text-xs">pts</div>
                </div>
              </motion.div>
            );
          })}

          {players.length === 0 && (
            <div className="text-center text-white/40 py-10">
              <p>Aucun joueur pour l'instant</p>
            </div>
          )}

          {/* Palmarès des mois précédents */}
          {activeTab === 'mois' && pastChampions.length > 0 && (
            <div className="mt-6 mb-2">
              <h3
                className="text-white/60 text-xs uppercase tracking-widest mb-3 text-center"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Palmarès
              </h3>
              {pastChampions.map(({ month, winners }) => (
                <div key={month} className="mb-4">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <FaCalendarAlt size={11} className="text-qfl-gold/70" />
                    <span className="text-qfl-gold/70 text-xs font-bold uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      {month}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {winners.map((w) => {
                      const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
                      return (
                        <div
                          key={w.rank}
                          className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2 border border-white/10"
                        >
                          <span className="text-base">{medals[w.rank]}</span>
                          <Avatar user={w} size="xs" />
                          <div className="flex-1 min-w-0">
                            <span
                              className="text-white text-sm font-bold truncate block"
                              style={{ fontFamily: "'Rajdhani', sans-serif" }}
                            >
                              {w.pseudo}
                            </span>
                            <span className="text-white/40 text-xs">
                              {Number(w.total_score).toLocaleString('fr-FR')} pts
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-qfl-gold text-xs font-bold">+{w.coins_reward}</span>
                            <span className="text-white/40 text-xs"> pièces</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bannière Premium */}
      <PremiumBanner className="px-4 pt-3 pb-1" />

      {/* Bouton continuer */}
      <div className="px-4 pt-3 pb-2">
        <Button variant="gold" size="lg" fullWidth onClick={() => navigate('/')}>
          CONTINUER
        </Button>
      </div>
    </div>
    </PageTransition>
  );
}

function PodiumItem({ player, rank, height }) {
  const navigate = useNavigate();
  const colors = { 1: '#f0b429', 2: '#c0cfe0', 3: '#cd8b3a' };
  const color = colors[rank] || '#94a3b8';
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };

  return (
    <div
      className="flex flex-col items-center gap-1 cursor-pointer"
      onClick={() => navigate(`/profile/${player.id || player._id}`)}
    >
      <span className="text-xl">{medals[rank]}</span>
      <Avatar user={player} size={rank === 1 ? 'lg' : 'md'} />
      <span
        className="text-xs font-bold truncate max-w-[70px] text-center"
        style={{ fontFamily: "'Rajdhani', sans-serif", color }}
      >
        {player.pseudo}
      </span>
      <div
        className="w-16 rounded-t-xl flex items-end justify-center pb-2"
        style={{
          height,
          background: `linear-gradient(to top, ${color}, ${color}88)`,
          boxShadow: `0 0 20px ${color}66, 0 -4px 12px ${color}44`,
        }}
      >
        <span
          className="text-xs font-black"
          style={{ fontFamily: "'Rajdhani', sans-serif", color: '#0d2b1a', fontWeight: 900 }}
        >
          {Number(player.total_score || player.score || 0).toLocaleString('fr-FR')}
        </span>
      </div>
    </div>
  );
}

export default Leaderboard;
